import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GA4DataLayerEvent, MetaEventParams, PlausibleFn, PlausibleOptions } from '../model/analytics';
import { consentCovers } from '../consent/consent';
import { MetaPixelService, PixelStore } from './meta-pixel.service';
import { GoogleAdsService } from './google-ads.service';

export interface AnalyticsContext {
  vendorName?: string;
  vendorId?: string;
  storeSlug?: string;
  customerType?: 'private' | 'business';
  environment?: 'production' | 'development';
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price?: number;
  quantity?: number;
  item_category?: string;
  affiliation?: string;
}

export interface EcommerceData {
  transaction_id?: string;
  value?: number;
  currency?: string;
  affiliation?: string;
  items: EcommerceItem[];
  /** On a purchase: an invoice order reaches Plausible as «Faktura bestilt», so «Purchase» means paid. */
  paymentType?: 'CreditCard' | 'Invoice';
}

/**
 * The share of an invoice order's value that gets paid: 98 % of invoiced value
 * since 2025 (CardAdmin export, 2026-11-05), less a margin for new buyers from
 * ads. The invoice order conversion is worth this share of what iGive earns.
 */
export const INVOICE_PAID_SHARE = 0.95;

/**
 * Single entry point for all analytics tracking in this application.
 * Do NOT call window.dataLayer, window.plausible, window.fbq or window.gtag directly — use this service.
 * Provides error isolation between GA4, Plausible, the Meta pixel and Google Ads, and dev-mode warnings for absent trackers.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private context: AnalyticsContext = {};
  private plausibleEnabled = environment.plausible?.enabled ?? false;

  constructor(private metaPixel: MetaPixelService, private googleAds: GoogleAdsService) {
    // Initialize Plausible queue for events fired before script loads
    this.initPlausibleQueue();
  }

  /**
   * Initialize event queue to capture events before Plausible script loads.
   * This prevents losing early events like view_item on page load.
   */
  private initPlausibleQueue(): void {
    if (typeof window === 'undefined' || !this.plausibleEnabled) return;

    // Create queue if Plausible isn't loaded yet
    window.plausible = window.plausible || function(event: string, options?: PlausibleOptions) {
      (window.plausible!.q = window.plausible!.q || []).push([event, options]);
    } as PlausibleFn;
  }

  /**
   * Set context (vendor info) for all subsequent events
   */
  setContext(context: AnalyticsContext): void {
    this.context = { ...this.context, ...context };
  }

  /**
   * Tells the trackers which store this page shows, or null when it could not
   * load one. The Meta pixel and Google Ads use it: they run for some stores
   * and not others, and send nothing until a page has declared a store that
   * qualifies.
   */
  setStore(store: PixelStore | null): void {
    try {
      this.metaPixel.setStore(store);
    } catch (e) {
      this.logError('Meta', 'setStore', e);
    }
    try {
      this.googleAds.setStore(store);
    } catch (e) {
      this.logError('GoogleAds', 'setStore', e);
    }
  }

  /**
   * Clear context (e.g., when user navigates to different store)
   */
  clearContext(): void {
    this.context = {};
  }

  /**
   * Track a custom event to both GA4 and Plausible
   * Uses try-catch isolation so one failure doesn't affect the other
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    const ga4Props = { ...properties, ...this.context };

    // GA4 via dataLayer — context spread as-is (camelCase), unchanged (isolated)
    try {
      this.pushToDataLayer({
        event: eventName,
        ...ga4Props,
        timestamp: Date.now()
      });
    } catch (e) {
      this.logError('GA4', eventName, e);
    }

    // Plausible — context mapped to snake_case via contextToPlausibleProps (isolated)
    try {
      const plausibleName = this.toPlausibleEventName(eventName);
      const plausibleProps = this.sanitizeProps({
        ...properties,
        ...this.contextToPlausibleProps(),
      });
      this.pushToPlausible(plausibleName, { props: plausibleProps });
    } catch (e) {
      this.logError('Plausible', eventName, e);
    }
  }

  /**
   * Resolves once every tracker injected after bootstrap has taken what was
   * queued from this page: the Meta pixel and Google Ads. Plausible is loaded
   * by index.html before Angular boots. The receipt awaits this
   * before leaving for the post-purchase page, so the purchase is reported
   * from the receipt. Both waits run side by side, each capped.
   */
  settled(): Promise<void> {
    return Promise.all([
      this.settledOne('Meta', () => this.metaPixel.settled()),
      this.settledOne('GoogleAds', () => this.googleAds.settled()),
    ]).then(() => undefined);
  }

  /**
   * The purchase the ad platforms are told: Google Ads' conversion and Meta's
   * Purchase, one figure for both. Its value is what iGive earns on a card-paid
   * order, never the order total, so it is not part of trackEcommerce. The
   * payment id is Google's transaction id and Meta's event id, so a later
   * server-side copy can be de-duplicated against either.
   */
  trackPaidPurchase(paymentId: string, earnings: number): void {
    try {
      this.googleAds.trackPurchase(paymentId, earnings);
    } catch (e) {
      this.logError('GoogleAds', 'purchase', e);
    }
    try {
      this.metaPixel.track('Purchase', { ...this.metaProductParams(), value: earnings, currency: 'NOK' }, paymentId);
    } catch (e) {
      this.logError('Meta', 'purchase', e);
    }
  }

  /**
   * The invoice order Google Ads is told, apart from «Kjøp»: placed, not paid,
   * so it is valued at the share of the earnings that invoices bring in. Meta
   * is told nothing; it counts paid orders only. The order id is the
   * transaction id.
   */
  trackInvoiceOrder(orderId: string, earnings: number): void {
    try {
      this.googleAds.trackInvoiceOrder(orderId, Math.round(earnings * INVOICE_PAID_SHARE * 100) / 100);
    } catch (e) {
      this.logError('GoogleAds', 'invoice_order', e);
    }
  }

  /**
   * Track ecommerce events (view_item, begin_checkout, purchase)
   * Sends to both GA4 (full ecommerce schema) and Plausible (simplified)
   */
  trackEcommerce(
    event: 'view_item' | 'begin_checkout' | 'purchase',
    data: EcommerceData
  ): void {
    const currency = data.currency || 'NOK';

    // GA4 - Full ecommerce schema (isolated)
    try {
      // Clear stale ecommerce data before pushing new event (GA4 best practice)
      this.pushToDataLayer({ ecommerce: null });
      this.pushToDataLayer({
        event,
        ecommerce: {
          transaction_id: data.transaction_id,
          currency,
          value: data.value,
          affiliation: data.affiliation,
          items: data.items.map(item => ({
            item_id: item.item_id,
            item_name: item.item_name,
            item_category: item.item_category || 'Gavekort',
            currency,
            quantity: item.quantity,
            price: item.price,
            affiliation: item.affiliation || data.affiliation
          }))
        }
      });
    } catch (e) {
      this.logError('GA4', event, e);
    }

    // Plausible - Simplified with revenue for purchases (isolated)
    // Context props come from contextToPlausibleProps() — no ad-hoc vendor/store_slug literals.
    try {
      const plausibleProps: Record<string, string | number | boolean> = {
        value: data.value,
        quantity: data.items.reduce((sum, i) => sum + (i.quantity ?? 1), 0),
        ...this.contextToPlausibleProps(),
      };

      if (data.transaction_id) {
        plausibleProps.transaction_id = data.transaction_id;
      }

      const plausibleEventMap: Record<string, string> = {
        'view_item': 'View Product',
        'begin_checkout': 'Begin Checkout',
        'purchase': 'Purchase'
      };

      if (event === 'purchase') {
        // Include revenue tracking for purchases
        const goal = data.paymentType === 'Invoice' ? 'Faktura bestilt' : plausibleEventMap[event];
        this.pushToPlausible(goal, {
          props: plausibleProps,
          revenue: { amount: data.value, currency }
        });
      } else {
        this.pushToPlausible(plausibleEventMap[event], { props: plausibleProps });
      }
    } catch (e) {
      this.logError('Plausible', event, e);
    }

    // Meta pixel - the card as the only product detail, no amount. Nothing
    // from `items` (names, prices, quantities) and nothing from the order
    // crosses here. Meta's Purchase is sent by trackPaidPurchase. (isolated)
    if (event !== 'purchase') {
      try {
        this.metaPixel.track(event === 'view_item' ? 'ViewContent' : 'InitiateCheckout', this.metaProductParams());
      } catch (e) {
        this.logError('Meta', event, e);
      }
    }
  }

  /** The card's slug as the only product detail Meta is told, or nothing when the slug is unknown. */
  private metaProductParams(): MetaEventParams {
    return this.context.storeSlug !== undefined
      ? { content_ids: [this.context.storeSlug], content_type: 'product' }
      : {};
  }

  /**
   * Track distribution funnel events
   * Note: `order_value` is the distribution-total dimension — it is kept distinct from
   * the ecommerce `value` key (purchase amount). Do NOT alias or rename it.
   */
  trackDistribution(
    event: 'started' | 'card_sent' | 'complete',
    properties?: Record<string, unknown>
  ): void {
    const ga4EventName = `distribution_${event}`;
    const ga4Props = { ...properties, ...this.context };

    // GA4 — context spread as-is (camelCase), unchanged (isolated)
    try {
      this.pushToDataLayer({
        event: ga4EventName,
        ...ga4Props,
        timestamp: Date.now()
      });
    } catch (e) {
      this.logError('GA4', ga4EventName, e);
    }

    // Plausible — context mapped to snake_case via contextToPlausibleProps (isolated)
    try {
      const plausibleEventMap: Record<string, string> = {
        'started': 'Distribution: Started',
        'card_sent': 'Distribution: Card Sent',
        'complete': 'Distribution: Complete'
      };

      this.pushToPlausible(plausibleEventMap[event], {
        props: this.sanitizeProps({
          ...properties,
          ...this.contextToPlausibleProps(),
        })
      });
    } catch (e) {
      this.logError('Plausible', event, e);
    }
  }

  /**
   * Track errors (GA4 only - not valuable in Plausible aggregate view)
   */
  trackError(errorType: string, error: unknown): void {
    try {
      this.pushToDataLayer({
        event: 'error',
        error_type: errorType,
        error_message: this.sanitizeErrorMessage(error),
        ...this.context,
        timestamp: Date.now()
      });
    } catch (e) {
      // Don't recursively log errors
      console.error('[Analytics] Failed to track error:', e);
    }
    // Intentionally not sent to Plausible
  }

  // ─────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────

  /**
   * Maps the camelCase AnalyticsContext to snake_case Plausible prop keys.
   * This is the single source of context props for all three Plausible emit paths.
   * Undefined context fields are omitted so Plausible receives no empty keys.
   * GA4 paths continue to receive context as-is (camelCase) — this mapper is Plausible-only.
   */
  private contextToPlausibleProps(): Record<string, string> {
    const result: Record<string, string> = {};
    if (this.context.vendorName !== undefined)  { result['vendor_name']   = this.context.vendorName; }
    if (this.context.vendorId   !== undefined)  { result['vendor_id']     = this.context.vendorId; }
    if (this.context.storeSlug  !== undefined)  { result['store_slug']    = this.context.storeSlug; }
    if (this.context.customerType !== undefined){ result['customer_type'] = this.context.customerType; }
    if (this.context.environment !== undefined) { result['environment']   = this.context.environment; }
    return result;
  }

  /**
   * The single chokepoint for all 31 GA4 call sites. Consent is read on every
   * push, not cached at construction, so an in-session grant takes effect
   * immediately. Events fired before a grant are dropped by design — no
   * buffer-and-replay — because replaying them would leak pre-consent
   * activity into GA4 the moment someone accepts. That includes events from
   * before the hosted consent banner has read a returning visitor's answer.
   */
  private pushToDataLayer(data: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    if (!consentCovers('googleAnalytics')) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data as GA4DataLayerEvent);
  }

  private pushToPlausible(eventName: string, options?: PlausibleOptions): void {
    if (!this.plausibleEnabled) return;   // disabled-by-config: fully silent
    if (typeof window === 'undefined') return;

    if (window.plausible) {
      window.plausible(eventName, options);
    } else if (!environment.production) {
      console.warn(`[Analytics:Plausible] Tracker absent — event "${eventName}" was not sent`);
    }
  }

  /**
   * Isolated like every other call into a tracker: a broken wait must never
   * hold the page that awaits it.
   */
  private settledOne(system: string, wait: () => Promise<void>): Promise<void> {
    try {
      return wait().catch((e: unknown) => this.logError(system, 'settled', e));
    } catch (e) {
      this.logError(system, 'settled', e);
      return Promise.resolve();
    }
  }

  /**
   * Convert snake_case GA4 event names to Title Case for Plausible
   */
  private toPlausibleEventName(eventName: string): string {
    return eventName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Sanitize properties for Plausible (only string/number/boolean allowed)
   * Also removes undefined/null values
   */
  private sanitizeProps(props?: Record<string, unknown>): Record<string, string | number | boolean> | undefined {
    if (!props) return undefined;

    const sanitized: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(props)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else if (value !== null && value !== undefined) {
        // Convert objects/arrays to string (with truncation for safety)
        const stringified = String(value);
        sanitized[key] = stringified.length > 100 ? stringified.substring(0, 100) + '...' : stringified;
      }
    }
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
  }

  /**
   * Safely extract error message without exposing sensitive data
   */
  private sanitizeErrorMessage(error: unknown): string {
    if (!error) return 'Unknown error';
    if (typeof error === 'string') return error.substring(0, 200);
    if (error instanceof Error) return error.message.substring(0, 200);
    if (typeof error === 'object' && error !== null && 'message' in error) {
      return String((error as { message: unknown }).message).substring(0, 200);
    }
    return 'Error (no message)';
  }

  /**
   * Log analytics errors to console (dev only)
   */
  private logError(system: string, event: string, error: unknown): void {
    if (!environment.production) {
      console.error(`[Analytics:${system}] Failed to track "${event}":`, error);
    }
  }
}
